/**
 * DataMappingViewerV2 - Enhanced AI data usage viewer with purpose grouping and entity summary
 *
 * Extends V1 DataMappingViewer with:
 * - Purpose grouping (DataUsagePurpose): items grouped by intent
 * - Entity summary: which entities were used, how many times, for what
 * - Category chips: insight (primary), persona (fallback), product, social_proof, synthesis, strategic
 * - Source priority indicator (1-4)
 *
 * Falls back to flat item list if no purposes defined (backward compatible).
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DataObjectIcon from '@mui/icons-material/DataObject';
import StorageIcon from '@mui/icons-material/Storage';
// Subpath import on purpose: the ui-components BARREL drags data-pickers +
// firebase-core into every jest suite that renders this viewer (and they read
// `import.meta.env` at module scope, which jest cannot parse).
import { SAccordion } from '@modules/ui-components/dialogs/SAccordion';

import type {
  DataMappingItemV2,
  DataUsagePurpose,
  BlockDataUsage,
  EntityUsage,
  EntityUsageMap,
} from '@segmently/schema-models/simplified';

import { InsightEvidenceSection } from './InsightEvidenceSection';
import {
  EVIDENCE_SECTION_TITLE,
  groupInsightCitations,
  type InsightEvidenceState,
} from './insightEvidence';

// ═══════════════════════════════════════════════════════
// CATEGORY STYLING
// ═══════════════════════════════════════════════════════

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  insight: { bg: '#ede7f6', text: '#4527a0' },   // D39: primary grounding (an @insight thesis)
  persona: { bg: '#e3f2fd', text: '#1565c0' },   // fallback grounding (raw @audience actor)
  product: { bg: '#e8f5e9', text: '#2e7d32' },
  social_proof: { bg: '#fff3e0', text: '#e65100' },
  synthesis: { bg: '#f3e5f5', text: '#7b1fa2' },
  strategic: { bg: '#fce4ec', text: '#c62828' },
};

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Primary (Persona/Product)',
  2: 'Secondary (Social Proof)',
  3: 'Synthesis',
  4: 'Strategic (Fallback)',
};

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

/**
 * Context carrying an optional sourcePath → human-label resolver down the
 * viewer's subtree. Strategy-blocks callers leave it undefined (raw paths
 * are fine); content-plan callers pass a HydratedSourceData-bound resolver
 * so items render with breadcrumb labels like "Audience › Actor › Fear".
 */
const ResolveLabelContext = React.createContext<
  ((sourcePath: string) => string | null) | null
>(null);

/** Check if item is a synthesis reference (self.dataUsage.dataMappingItems[...]) */
const isSynthesisItem = (item: DataMappingItemV2): boolean =>
  item.sourcePath?.startsWith('self.dataUsage') ?? false;

/** Single data mapping item — shows value prominently, sourcePath as small ID */
const DataMappingItemCard: React.FC<{
  item: DataMappingItemV2;
  compact?: boolean;
}> = ({ item, compact }) => {
  const categoryColor = item.category ? CATEGORY_COLORS[item.category] : undefined;
  const synthesis = isSynthesisItem(item);
  const resolveLabel = React.useContext(ResolveLabelContext);
  const breadcrumbLabel = item.sourcePath && resolveLabel
    ? resolveLabel(item.sourcePath)
    : null;

  return (
    <Box
      sx={{
        p: compact ? 0.75 : 1,
        borderRadius: 1,
        bgcolor: synthesis ? 'action.selected' : 'action.hover',
        borderLeft: synthesis ? '3px solid' : 'none',
        borderColor: synthesis ? 'primary.main' : undefined,
      }}
    >
      {/* Breadcrumb — human-readable entity chain when a resolver is wired in */}
      {breadcrumbLabel && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'primary.main', fontWeight: 500, mb: 0.25 }}
        >
          {breadcrumbLabel}
        </Typography>
      )}

      {/* Value — the actual content, shown prominently */}
      {item.value && (
        <Typography variant="body2" sx={{ fontWeight: synthesis ? 600 : 400, mb: 0.5 }}>
          {synthesis ? `🔗 ${item.value}` : item.value}
        </Typography>
      )}

      {/* Justification — why this data was used */}
      {item.justification && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
          {item.justification}
        </Typography>
      )}

      {/* sourcePath — technical ID, shown small */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.65rem',
            color: 'text.disabled',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.sourcePath}
        </Typography>
        {item.category && (
          <Chip
            label={item.category}
            size="small"
            sx={{
              height: 16,
              fontSize: '0.6rem',
              bgcolor: categoryColor?.bg,
              color: categoryColor?.text,
            }}
          />
        )}
        {item.sourcePriority && (
          <Chip
            label={`P${item.sourcePriority}`}
            size="small"
            variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem' }}
          />
        )}
      </Box>
    </Box>
  );
};

/** Purpose group section */
const PurposeGroup: React.FC<{
  purpose: DataUsagePurpose;
  items: DataMappingItemV2[];
}> = ({ purpose, items }) => {
  const [expanded, setExpanded] = useState(false);
  const categoryColor = purpose.category ? CATEGORY_COLORS[purpose.category] : undefined;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <GroupWorkIcon sx={{ fontSize: 18, mr: 1, color: categoryColor?.text ?? 'text.secondary' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {purpose.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {purpose.summary}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
          <Chip label={`${items.length} items`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          {purpose.category && (
            <Chip
              label={purpose.category}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: categoryColor?.bg,
                color: categoryColor?.text,
              }}
            />
          )}
        </Stack>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item, idx) => (
            <DataMappingItemCard key={idx} item={item} />
          ))}
          {purpose.appliedTo && purpose.appliedTo.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Applied to: {purpose.appliedTo.join(', ')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

/** Entity usage summary */
const EntitySummarySection: React.FC<{ entitySummary: EntityUsageMap }> = ({ entitySummary }) => {
  const allEntities: Array<{ type: string; usage: EntityUsage }> = useMemo(() => {
    const result: Array<{ type: string; usage: EntityUsage }> = [];
    const types: Array<[string, EntityUsage[] | undefined]> = [
      ['Audiences', entitySummary.audiences],
      ['Tasks', entitySummary.tasks],
      ['Study Cases', entitySummary.studyCases],
      ['Reviews', entitySummary.reviews],
      ['Ratings', entitySummary.ratings],
      ['Big Numbers', entitySummary.bigNumbers],
    ];
    for (const [type, usages] of types) {
      for (const usage of (usages ?? [])) {
        result.push({ type, usage });
      }
    }
    return result;
  }, [entitySummary]);

  if (allEntities.length === 0) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <StorageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="subtitle2">Entity Summary ({allEntities.length})</Typography>
      </Box>
      <Stack spacing={0.5}>
        {allEntities.map(({ type, usage }, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Chip label={type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Typography variant="body2" sx={{ flex: 1 }}>{usage.entityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {usage.itemCount} ref{usage.itemCount !== 1 ? 's' : ''}
            </Typography>
            {usage.screens && usage.screens.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                on {usage.screens.length} screen{usage.screens.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export interface DataMappingViewerV2Props {
  /** Block data usage (V2 format with purposes + entitySummary) */
  dataUsage?: BlockDataUsage;

  /** Default expanded state */
  defaultExpanded?: boolean;

  /** Custom title */
  title?: string;

  /**
   * Optional resolver that turns a `sourcePath` into a human-readable
   * breadcrumb label (e.g. "Audience › Actor › Fear"). When present, each
   * item card renders the breadcrumb above the raw monospace path. Strategy
   * blocks pass `undefined` (back-compat — shows raw paths as before);
   * content-plan passes a HydratedSourceData-bound `resolveSourcePath` adapter.
   */
  resolveLabel?: (sourcePath: string) => string | null;

  /**
   * W13 DM2 — the pinned analysis behind this screen's `@insight[…]` citations.
   *
   * OPT-IN: passing it (even with an empty `byAnalysis`, e.g. while the read is
   * in flight or after it failed) turns the insight citations into the
   * «Why this copy» rows — thesis, its quotes as sub-rows, lone quotes under
   * their warning heading. Consumers that pass NOTHING render exactly as before,
   * insight entries included.
   */
  insightEvidence?: InsightEvidenceState;
}

export const DataMappingViewerV2: React.FC<DataMappingViewerV2Props> = ({
  dataUsage,
  defaultExpanded = false,
  title = 'AI Data Usage',
  resolveLabel,
  insightEvidence,
}) => {
  // V2 only: support both `items` (schema canonical) and `dataMappingItems` (prompt output alias)
  const items = dataUsage?.items ?? (dataUsage as any)?.dataMappingItems ?? [];
  const purposes: any[] = dataUsage?.purposes ?? [];
  const entitySummary: any = dataUsage?.entitySummary;

  const hasPurposes = purposes.length > 0;
  // Handle both string entitySummary (AI output) and EntityUsageMap (typed schema)
  const hasEntitySummary = entitySummary && (
    typeof entitySummary === 'string'
      ? entitySummary.length > 0
      : Object.values(entitySummary).some((arr: any) => arr && arr.length > 0)
  );

  if (items.length === 0 && !hasPurposes) return null;

  // Group items by purpose — handle both string[] (AI output) and DataUsagePurpose[] (typed schema)
  const purposeItems = useMemo(() => {
    if (!hasPurposes) return new Map<string, DataMappingItemV2[]>();
    const map = new Map<string, DataMappingItemV2[]>();
    for (const purpose of purposes) {
      if (typeof purpose === 'string') {
        map.set(purpose, []);
        continue;
      }
      const grouped = (purpose.itemIndices ?? [])
        .map((idx: number) => items[idx])
        .filter(Boolean);
      map.set(purpose.id, grouped);
    }
    return map;
  }, [purposes, items, hasPurposes]);

  // Items not in any purpose group
  const ungroupedItems = useMemo(() => {
    if (!hasPurposes) return items;
    const groupedIndices = new Set(
      purposes.flatMap(p => typeof p === 'string' ? [] : (p.itemIndices ?? []))
    );
    return items.filter((_: any, idx: number) => !groupedIndices.has(idx));
  }, [items, purposes, hasPurposes]);

  // W13 DM2: with the pinned analysis wired in, insight citations leave the flat
  // item list and become the «Why this copy» rows (thesis + its quotes); every
  // other entry keeps today's card. Plain consts, not a memo: the split is O(n)
  // over a screen's handful of entries, and this component's memos already sit
  // after an early return — one more conditional hook would be one too many.
  const groupedEvidence = insightEvidence ? groupInsightCitations(ungroupedItems) : null;
  const flatItems: any[] = groupedEvidence ? groupedEvidence.rest : ungroupedItems;

  // Category summary
  const categorySummary = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of items) {
      const cat = item.category ?? 'unknown';
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  return (
    <ResolveLabelContext.Provider value={resolveLabel ?? null}>
    <SAccordion
      title={title}
      defaultExpanded={defaultExpanded}
      collapsible
      icon={<DataObjectIcon sx={{ fontSize: 18 }} />}
    >
      <Box data-testid="data-mapping-viewer-v2" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Category summary chips */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip label={`${items.length} total`} size="small" variant="outlined" />
          {Object.entries(categorySummary).map(([cat, count]) => {
            const color = CATEGORY_COLORS[cat];
            return (
              <Chip
                key={cat}
                label={`${cat}: ${count}`}
                size="small"
                sx={{
                  bgcolor: color?.bg,
                  color: color?.text,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
            );
          })}
        </Stack>

        {/* Entity summary */}
        {hasEntitySummary && (
          typeof entitySummary === 'string'
            ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{entitySummary}</Typography>
            : <EntitySummarySection entitySummary={entitySummary!} />
        )}

        {/* Purpose groups */}
        {hasPurposes && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Functions ({purposes.length})
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {purposes.map((purpose: any, idx: number) => {
                // String format (AI output): render as simple chips
                if (typeof purpose === 'string') {
                  return <Chip key={idx} label={purpose} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />;
                }
                // Object format (typed schema): render as expandable groups
                return (
                  <PurposeGroup
                    key={purpose.id}
                    purpose={purpose}
                    items={purposeItems.get(purpose.id) ?? []}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* «Why this copy» — the cited evidence, thesis by thesis (W13 DM2) */}
        {groupedEvidence && insightEvidence && (
          <InsightEvidenceSection
            grouped={groupedEvidence}
            evidence={insightEvidence}
            // The panel name is printed once: an accordion already titled
            // «Why this copy» owns it (REVIEW tier 2 should-fix).
            showTitle={title !== EVIDENCE_SECTION_TITLE}
          />
        )}

        {/* Items: split into synthesis groups and individual items */}
        {flatItems.length > 0 && (() => {
          // Separate synthesis items from regular items
          const synthItems = flatItems.filter((i: any) => isSynthesisItem(i));
          const regularItems = flatItems.filter((i: any) => !isSynthesisItem(i));

          // For each synthesis item, find which regular items it references
          const parseSynthIndices = (path: string): number[] => {
            const match = path.match(/\[([0-9, ]+)\]/);
            if (!match) return [];
            return match[1].split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !isNaN(n));
          };

          return (
            <Box>
              {/* Regular items first */}
              {regularItems.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: synthItems.length > 0 ? 1.5 : 0 }}>
                  {regularItems.map((item: any, idx: number) => (
                    <DataMappingItemCard key={`reg-${idx}`} item={item} />
                  ))}
                </Stack>
              )}

              {/* Synthesis groups — grouped items with their references */}
              {synthItems.map((synthItem: any, sIdx: number) => {
                const refIndices = parseSynthIndices(synthItem.sourcePath);
                const referencedItems = refIndices
                  .map((i: number) => items[i])
                  .filter(Boolean);

                return (
                  <Paper key={`synth-${sIdx}`} variant="outlined" sx={{ p: 1, mt: 1, borderColor: 'primary.main', borderStyle: 'dashed' }}>
                    {/* Synthesis header with justification */}
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      🔗 {synthItem.value}
                    </Typography>
                    {synthItem.justification && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        {synthItem.justification}
                      </Typography>
                    )}
                    {/* Referenced items shown compact */}
                    {referencedItems.length > 0 && (
                      <Stack spacing={0.25} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {referencedItems.map((refItem: any, rIdx: number) => (
                          <DataMappingItemCard key={`ref-${rIdx}`} item={refItem} compact />
                        ))}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
            </Box>
          );
        })()}
      </Box>
    </SAccordion>
    </ResolveLabelContext.Provider>
  );
};
